import React from 'react'
import { localizedText } from '../../utils/localized';
import { getCatalogLabel, getPriorityAppearance, getPriorityLabel, getStatusAppearance, getStatusCategory, getStatusLabel } from '../../constants/ticket.js'
import AuthAvatar from '../ui/AuthAvatar'
import TruncatedText from '../ui/TruncatedText'
import AbsatBotBadge, { isAbsatBot } from '../ui/AbsatBotBadge'
import { formatAssigneeNames, getTicketAssignees } from '../../utils/ticketAssignees'

const TicketsListItem = ({ item, lang, gridTemplate, supportSettings, tenants = [], assignees = [], nowMs = null, selectable = false, selected = false, onToggleSelected = null, showStatus = false, showSprint = false }) => {
    const pad2 = (value) => String(value).padStart(2, '0');
    const formatDateTime = (raw) => {
        const date = raw instanceof Date ? raw : raw ? new Date(raw) : null;
        if (!date || Number.isNaN(date.getTime())) return '-';
        return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    };
    const createDate = (() => {
        return formatDateTime(item?.createdAt);
    })();
    const expectedAtLabel = (() => {
        return formatDateTime(item?.expectedAt);
    })();
    const expectedAtDate = (() => {
        const date = item?.expectedAt ? new Date(item.expectedAt) : null;
        if (!date || Number.isNaN(date.getTime())) return null;
        return date;
    })();

    const formatDurationMs = (valueMs, { hideZero = false } = {}) => {
        const ms = Math.max(0, Number(valueMs) || 0);
        const totalMinutes = Math.floor(ms / 60000);
        if (hideZero && totalMinutes <= 0) return null;
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
        const minutes = totalMinutes % 60;
        if (lang === 'ar') {
            const parts = [];
            if (days) parts.push(`${days}ي`);
            if (hours || days) parts.push(`${hours}س`);
            parts.push(`${minutes}د`);
            return parts.join(' ');
        }
        const parts = [];
        if (days) parts.push(`${days}d`);
        if (hours || days) parts.push(`${hours}h`);
        parts.push(`${minutes}m`);
        return parts.join(' ');
    };

    const actualDurationLabel = (() => {
        const statusValue = item?.statusId || item?.status;
        const statusCategory = getStatusCategory(supportSettings?.statuses, statusValue);
        if (!['new', 'review', 'pending', 'processing', 'completed'].includes(statusCategory)) return null;

        const startAt = (() => {
            if (statusCategory === 'processing' || statusCategory === 'completed') {
                const processingAt = item?.processingAt
                    ? new Date(item.processingAt)
                    : item?.modifiedAt || item?.updatedAt
                        ? new Date(item?.modifiedAt || item?.updatedAt)
                        : null;
                return processingAt;
            }
            const raw = item?.modifiedAt || item?.updatedAt || item?.createdAt || null;
            return raw ? new Date(raw) : null;
        })();
        if (!startAt || Number.isNaN(startAt.getTime())) return null;

        const now = Number.isFinite(Number(nowMs)) ? Number(nowMs) : Date.now();
        if (statusCategory === 'completed') {
            const completedAt = item?.completedAt ? new Date(item.completedAt) : null;
            if (!completedAt || Number.isNaN(completedAt.getTime())) return null;
            return formatDurationMs(completedAt.getTime() - startAt.getTime(), { hideZero: true });
        }
        return formatDurationMs(now - startAt.getTime(), { hideZero: true });
    })();
    const isOverdue = (() => {
        if (!expectedAtDate) return false;
        const statusValue = item?.statusId || item?.status;
        const statusCategory = getStatusCategory(supportSettings?.statuses, statusValue);
        if (!['new', 'review', 'pending', 'processing', 'completed'].includes(statusCategory)) return false;
        const now = Number.isFinite(Number(nowMs)) ? Number(nowMs) : Date.now();
        if (statusCategory === 'new' || statusCategory === 'review' || statusCategory === 'pending' || statusCategory === 'processing') {
            return now > expectedAtDate.getTime();
        }
        const completedAt = item?.completedAt ? new Date(item.completedAt) : null;
        if (!completedAt || Number.isNaN(completedAt.getTime())) return false;
        return completedAt.getTime() > expectedAtDate.getTime();
    })();
    const updatedDate = (() => {
        return formatDateTime(item?.modifiedAt || item?.updatedAt || null);
    })();
    const _section = getCatalogLabel(supportSettings?.sections, item.sectionId, lang);
    const _product = String(item?.requestType || '').toLowerCase() === 'quote-request'
        ? item?.productName || '-'
        : getCatalogLabel(supportSettings?.products, item.ProductId, lang);
    const priorityMeta = getPriorityAppearance(item?.priority || 'medium');
    const visibilityMeta = String(item?.visibility || 'external').toLowerCase() === 'internal'
        ? { label: lang === 'ar' ? 'داخلية' : 'Internal', bg: '#eef2ff', color: '#4f46e5', dot: '#6366f1' }
        : { label: lang === 'ar' ? 'خارجية' : 'External', bg: '#ecfdf5', color: '#047857', dot: '#10b981' };
    const isQuoteRequest = String(item?.requestType || '').toLowerCase() === 'quote-request';
    const quoteRequestLabel = lang === 'ar' ? 'طلب عرض توضيحي' : 'Quote Request';
    const tenantName = item?.tenantName || tenants.find((tenant) => tenant?._id === item?.tenantId)?.tenantName || item?.tenantId || '-';
    const normalizeText = (value) => String(value || '').trim().toLowerCase();
    const ticketAssignees = getTicketAssignees(item).map((entry, index) => {
        const entryId = normalizeText(entry.userId);
        const entryName = normalizeText(entry.name);
        const option = assignees.find((admin) => {
            const optionId = normalizeText(admin?.value);
            const optionLabel = normalizeText(admin?.label);
            return Boolean(
                (entryId && optionId === entryId) ||
                (entryId && optionLabel === entryId) ||
                (entryName && optionLabel === entryName)
            );
        });
        return {
            userId: entry.userId,
            name: entry.name || option?.label || entry.userId,
            avatar: ((index === 0 ? item?.assignedToAvatar : '') || option?.avatar || option?.userPic || option?.image || '')
        };
    });
    const assigneeNames = formatAssigneeNames(ticketAssignees.map((entry) => entry.name), lang);
    const MAX_VISIBLE_AVATARS = 3;
    const projectManagerId = normalizeText(item?.projectManagerId);
    const projectManagerName = normalizeText(item?.projectManagerName);
    const projectManager = assignees.find((admin) => {
        const optionId = normalizeText(admin?.value);
        const optionLabel = normalizeText(admin?.label);
        return Boolean(
            (projectManagerId && optionId === projectManagerId) ||
            (projectManagerId && optionLabel === projectManagerId) ||
            (projectManagerName && optionLabel === projectManagerName)
        );
    });
    const projectManagerDisplayName = item.projectManagerName || projectManager?.label || item.projectManagerId || '';
    // آخر من عدّل: من الحقل الجديد، وللتذاكر القديمة نرجع لكاتب آخر رد.
    const lastDetail = Array.isArray(item?.details) && item.details.length ? item.details[item.details.length - 1] : null;
    const lastModifiedId = normalizeText(item?.lastModifiedBy || lastDetail?.createdBy);
    const lastModifiedOption = lastModifiedId ? assignees.find((admin) => normalizeText(admin?.value) === lastModifiedId) : null;
    const lastModifiedName = (item?.lastModifiedBy ? item?.lastModifiedByName : '') || lastModifiedOption?.label || '';
    const lastModifiedAtLabel = formatDateTime(item?.lastModifiedAt || (item?.lastModifiedBy ? null : lastDetail?.createdAt));
    // المنشئ: الاسم المحفوظ على التذكرة، وللتذاكر القديمة من قائمة الموظفين، وإلا فالعميل اللي فتحها من البوابة.
    const creatorId = normalizeText(item?.createdBy);
    const creatorOption = creatorId ? assignees.find((admin) => normalizeText(admin?.value) === creatorId) : null;
    const creatorName = isQuoteRequest
        ? (item?.customerName || (lang === 'ar' ? 'زائر الموقع' : 'Website visitor'))
        : (item?.createdByName || creatorOption?.label || item?.customerName || '');
    const creatorAvatar = creatorOption?.avatar || creatorOption?.userPic || creatorOption?.image || '';
    const statusValue = item?.statusId || item?.status;
    const statusMeta = getStatusAppearance(supportSettings?.statuses, statusValue);
    const statusLabel = getStatusLabel(supportSettings?.statuses, statusValue, lang);
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                gap: 8,
                alignItems: 'center',
                padding: '14px 16px',
                fontSize: 12,
                color: '#475569',
                background: '#fff',
                borderBottom: '1px solid #e2e8f0',
                transition: 'all .15s'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8fafc';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
            }}
        >
            {selectable && (
                <div
                    data-no-row-click="true"
                    style={{ display: 'flex', justifyContent: 'center' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelected?.(item?._id);
                    }}
                >
                    <input
                        type="checkbox"
                        checked={Boolean(selected)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            e.stopPropagation();
                            onToggleSelected?.(item?._id);
                        }}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                </div>
            )}
            <div style={{ textAlign: 'center', fontWeight: 700, color: '#0f172a' }}>{item.ticketNumber || '-'}</div>
            <div style={{ textAlign: lang === 'ar' ? 'right' : 'left', overflow: 'hidden', minWidth: 0 }}>
                <TruncatedText text={localizedText(item, 'subject', lang)} style={{ fontWeight: 700, color: '#0f172a' }} />
                {/* العميل تحت العنوان (بدل عمود منفصل) عشان العنوان ياخد مساحة أكبر. */}
                <TruncatedText text={tenantName} style={{ marginTop: 2 }} />
                {isQuoteRequest && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', marginTop: 4, padding: '3px 8px', borderRadius: 999, background: '#fff7ed', color: '#c2410c', fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {quoteRequestLabel}
                    </span>
                )}
                {showSprint && item?.sprintId && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', marginTop: 4, marginInlineEnd: 4, padding: '3px 8px', borderRadius: 999, background: '#ecfeff', color: '#0e7490', fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {lang === 'ar' ? 'دورة' : 'Sprint'} {item?.sprintNumber ?? ''}
                    </span>
                )}
                {item?.projectId && (
                    <TruncatedText
                        as="span"
                        text={`${lang === 'ar' ? 'مشروع' : 'Project'} #${item?.projectNumber ?? '-'} ${item?.projectTitle ? `— ${item.projectTitle}` : ''}`}
                        style={{ display: 'inline-block', maxWidth: '100%', marginTop: 4, padding: '3px 8px', borderRadius: 999, background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 800 }}
                    />
                )}
            </div>
            <div style={{ direction: lang === 'ar' ? 'rtl' : 'ltr', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, minWidth: 0 }}>
                {isAbsatBot(item) ? (
                    <div data-testid="ticket-creator" style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                        <span style={{ color: '#94a3b8', flexShrink: 0, fontSize: 10.5, fontWeight: 700 }}>{lang === 'ar' ? 'أنشأها:' : 'By:'}</span>
                        <AbsatBotBadge size="sm" scheduled={Boolean(item?.scheduledJobId)} lang={lang} />
                    </div>
                ) : creatorName && (
                    <div
                        data-testid="ticket-creator"
                        title={`${lang === 'ar' ? 'أنشأها' : 'Created by'} ${creatorName}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, fontSize: 10.5, color: '#334155', fontWeight: 700 }}
                    >
                        <AuthAvatar src={creatorAvatar} alt={creatorName} size={18} fontSize={8} style={{ border: '1px solid #e2e8f0', flexShrink: 0 }} />
                        <span style={{ color: '#94a3b8', flexShrink: 0 }}>{lang === 'ar' ? 'أنشأها:' : 'By:'}</span>
                        <TruncatedText as="span" text={creatorName} />
                    </div>
                )}
                <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, minWidth: 0, fontSize: 11, color: '#6366f1', fontWeight: 600 }}
                >
                    {ticketAssignees.length === 0 ? (
                        <span style={{ color: '#94a3b8' }}>-</span>
                    ) : (
                        <>
                            <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                                {ticketAssignees.slice(0, MAX_VISIBLE_AVATARS).map((entry, index) => (
                                    <AuthAvatar
                                        key={entry.userId}
                                        src={entry.avatar}
                                        alt={entry.name}
                                        size={22}
                                        style={{ border: '2px solid #fff', marginInlineStart: index === 0 ? 0 : -8, position: 'relative', zIndex: MAX_VISIBLE_AVATARS - index }}
                                    />
                                ))}
                                {ticketAssignees.length > MAX_VISIBLE_AVATARS && (
                                    <span style={{ width: 22, height: 22, borderRadius: '50%', marginInlineStart: -8, background: '#eef2ff', border: '2px solid #fff', color: '#4f46e5', fontSize: 9, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                        +{ticketAssignees.length - MAX_VISIBLE_AVATARS}
                                    </span>
                                )}
                            </span>
                            <TruncatedText as="span" text={assigneeNames} />
                        </>
                    )}
                </div>
                {projectManagerDisplayName && (
                    <TruncatedText
                        text={`${lang === 'ar' ? 'مدير المشروع: ' : 'PM: '}${projectManagerDisplayName}`}
                        style={{ fontSize: 10, color: '#0f766e', fontWeight: 800 }}
                    />
                )}
                {lastModifiedName && (
                    <div
                        title={`${lang === 'ar' ? 'آخر تعديل بواسطة' : 'Last modified by'} ${lastModifiedName}${lastModifiedAtLabel !== '-' ? ` — ${lastModifiedAtLabel}` : ''}`}
                        style={{ alignSelf: 'flex-start', maxWidth: '100%', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 999, background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', fontSize: 10, fontWeight: 800, overflow: 'hidden', whiteSpace: 'nowrap' }}
                    >
                        <span style={{ flexShrink: 0 }}>✎</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{lastModifiedName}</span>
                    </div>
                )}
            </div>
            <div style={{ textAlign: lang === 'ar' ? 'right' : 'left', whiteSpace: 'normal', lineHeight: 1.35, wordBreak: 'break-word' }}>
                <div style={{ fontWeight: 500, color: '#0f172a' }}>{_section}</div>
                <div style={{ marginTop: 2, fontSize: 11, fontWeight: 400, color: '#64748b' }}>{_product}</div>
            </div>
            {showStatus && (
                <div data-testid="ticket-status" style={{ textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, background: statusMeta.bg, color: statusMeta.color, fontWeight: 800, whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{ width: 7, height: 7, borderRadius: 999, background: statusMeta.dot || statusMeta.color, flexShrink: 0 }} />
                        {statusLabel || '-'}
                    </span>
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                {/* نوع التذكرة (داخلية / خارجية) فوق الأولوية، بنفس ألوان صفحة التذكرة. */}
                <span
                    data-testid="ticket-visibility"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 999, background: visibilityMeta.bg, color: visibilityMeta.color, fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap' }}
                >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: visibilityMeta.dot }} />
                    {visibilityMeta.label}
                </span>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '5px 10px',
                    borderRadius: 999,
                    background: priorityMeta.bg,
                    color: priorityMeta.color,
                    fontWeight: 800,
                    whiteSpace: 'nowrap'
                }}>
                    {getPriorityLabel(item?.priority || 'medium', lang)}
                </span>
            </div>
            <div style={{ textAlign: 'center', lineHeight: 1.35 }}>
                <div style={{ fontWeight: 500, color: '#0f172a' }}>{createDate}</div>
                <div style={{ marginTop: 2, fontSize: 11, fontWeight: 400, color: '#64748b' }}>{updatedDate}</div>
            </div>
            <div style={{ textAlign: 'center', lineHeight: 1.35 }}>
                <div style={{ fontWeight: 800, color: '#16a34a' }}>
                    {expectedAtLabel}
                </div>
                {actualDurationLabel && (
                    <div style={{ marginTop: 2, fontSize: 12, fontWeight: 800, color: isOverdue ? '#f59e0b' : '#dc2626' }}>
                        {actualDurationLabel}
                    </div>
                )}
            </div>
        </div>
    )
}

export default TicketsListItem
